import { relations } from 'drizzle-orm'
import {
  bigint,
  decimal,
  index,
  integer,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getId,
  getIdReference,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { customers } from './customer'
import { formSubmissions } from './formSubmission'
import { subscriptions } from './subscription'
import { opportunityTags } from './tag'
import { users } from './user'
import { visits } from './visit'

export const opportunityTypeEnum = pgEnum('opportunity_type', [
  'household',
  'housing_company',
  'company',
])

export const opportunitySourceEnum = pgEnum('opportunity_source', [
  'app_download',
  'website',
  'resident_app',
  'referral',
  'sales_call',
  'waitlist',
  'visit',
  'other',
])

export const opportunitySaleTypeEnum = pgEnum('opportunity_sale_type', [
  'subscription',
  'single_time',
])

export const opportunityStageEnum = pgEnum('opportunity_stage', [
  'new',
  'contacted',
  'survey_visit',
  'offer',
  'won',
  'lost',
  'disqualified',
])

export const opportunities = pgTable(
  'opportunities',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    type: opportunityTypeEnum().notNull().default('household'),
    saleType: opportunitySaleTypeEnum().notNull().default('subscription'),
    contextName: text(), // Optional: Company name or additional context
    stage: opportunityStageEnum().notNull().default('new'),
    source: opportunitySourceEnum().notNull().default('website'),

    // Opportunity-specific fields
    score: integer().default(0),
    estimatedValue: decimal({ precision: 10, scale: 2 }),
    data: typedJsonb('data'), // Stores partial address, property info, preferences
    // `internal` prefix hides this from non-staff roles. Today opportunities
    // are officer-only but this future-proofs against wider exposure.
    internalAcquisitionData: typedJsonb('internal_acquisition_data'),

    // Activity tracking
    lastActivityAt: getTimestampWithTimezone(),
    nextActionAt: getTimestampWithTimezone(),
    assignedToId: getNullableIdReference(() => users.id),
    reportedById: getNullableIdReference(() => users.id),

    // Conversion tracking
    convertedAt: getTimestampWithTimezone(),
    customerId: getNullableIdReference(() => customers.id),

    // Originating visit that created this opportunity (e.g. maintenance recommendation)
    // Plain bigint to avoid circular TS reference (FK constraint exists in DB migration)
    visitId: bigint({ mode: 'number' }),

    // Form submission this opportunity was created from (via inbox).
    // Provides a link back to the raw submission that produced the lead.
    formSubmissionId: getNullableIdReference(() => formSubmissions.id),

    notes: text(),
  },
  (table) => [...getBaseIndexes('opportunities', table)],
)

export const opportunityStageHistory = pgTable(
  'opportunity_stage_history',
  {
    id: getId(),
    opportunityId: getIdReference(() => opportunities.id),
    createdAt: getTimestampWithTimezone().notNull().defaultNow(),
    createdByUserId: getIdReference(() => users.id),
    stage: opportunityStageEnum().notNull(),
  },
  (table) => [
    index('opportunity_stage_history_opportunity_id_idx').on(
      table.opportunityId,
    ),
    index('opportunity_stage_history_created_at_idx').on(table.createdAt),
  ],
)

export const opportunitiesRelations = relations(
  opportunities,
  ({ one, many }) => ({
    user: one(users, {
      fields: [opportunities.userId],
      references: [users.id],
      relationName: 'opportunityUser',
    }),
    assignedTo: one(users, {
      fields: [opportunities.assignedToId],
      references: [users.id],
      relationName: 'opportunityAssignedTo',
    }),
    reportedBy: one(users, {
      fields: [opportunities.reportedById],
      references: [users.id],
      relationName: 'opportunityReportedBy',
    }),
    customer: one(customers, {
      fields: [opportunities.customerId],
      references: [customers.id],
    }),
    formSubmission: one(formSubmissions, {
      fields: [opportunities.formSubmissionId],
      references: [formSubmissions.id],
    }),
    originatingVisit: one(visits, {
      fields: [opportunities.visitId],
      references: [visits.id],
      relationName: 'opportunityOriginatingVisit',
    }),
    stageHistory: many(opportunityStageHistory),
    visits: many(visits),
    subscriptions: many(subscriptions),
    opportunityTags: many(opportunityTags),
  }),
)

// We also need the reverse relation, so that Drizzle understands how to fetch relations
export const opportunityStageHistoryRelations = relations(
  opportunityStageHistory,
  ({ one }) => ({
    opportunity: one(opportunities, {
      fields: [opportunityStageHistory.opportunityId],
      references: [opportunities.id],
    }),
    createdBy: one(users, {
      fields: [opportunityStageHistory.createdByUserId],
      references: [users.id],
    }),
  }),
)
